import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import AdyenCheckout from "@adyen/adyen-web";
import { toast } from 'react-toastify';

import styles from './Checkout.module.scss';
import '@adyen/adyen-web/dist/adyen.css';

import { request } from "../../shared/request";
import PopupInfo from "../../components/PopupInfo/PopupInfo";
import {ENV, ERROR_MAINTENANCE, OFFER_TYPES} from "../../shared/constants";
import { withResponseHandling } from "../../shared/responseHandler";
import Loading from "../../components/Loading/Loading";
import ContextMenuMobile from "../../components/ContextMenu/ContextMenuMobile";

const Checkout = ({ config, windowSize }) => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    let isFirstPageView = useRef(true);
    const [error, setError] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    
    // Get player and offer info from localStorage
    let selectedOffer = localStorage.getItem('selectedOffer');
    selectedOffer = JSON.parse(selectedOffer);
    let player = localStorage.getItem('playerInfo');
    player = JSON.parse(player);

    // Get URL parameters (used when coming from CheckoutInit)
    const offerId = searchParams.get('oid');
    const offerType = searchParams.get('otype');
    const source = searchParams.get('source');

    // If URL params exist, use them to set selectedOffer
    if (offerId && offerType) {
        selectedOffer = {
            offerId: offerId,
            offerType: offerType,
        };
    }

    // Validation deliberately lives in the mount effect below - never in the
    // render body. navigate() during render updates router state mid-render,
    // and because this component re-reads `selectedOffer` from localStorage on
    // every render (which onPaymentCompleted clears), it looped
    // render -> navigate -> render and crashed with React error #185
    // ("Maximum update depth exceeded") right after a successful payment.

    const initAdyenSession = async () => {
        const requestParams = {
            ...selectedOffer,
            saveEmailAgreed: state?.saveEmailAgreed || null,
            wantsReceipt: state?.wantsReceipt || null,
            email: state?.email || null,
            sourceType: source || null
        }
        try {
            await withResponseHandling(
                () => request.post(`initAdyenSession/${player.id}`, requestParams),
                {
                    navigate,
                    onSuccess: async (response) => {
                        const parsedSessionData = JSON.parse(response.SessionDataJson);
                        const configuration = {
                            environment: ENV, // Change to 'live' for the live environment.
                            clientKey: response.AdyenClientId, // Public key used for client-side authentication: https://docs.adyen.com/development-resources/client-side-authentication
                            session: parsedSessionData,
                            onPaymentCompleted: (result, component) => {
                                // console.info(result, component);
                                localStorage.removeItem('selectedOffer');
                                if (result.resultCode == 'Authorised') {
                                    navigate('/checkout-result', {state: {success: true, isEventPass: +selectedOffer.offerType === +OFFER_TYPES.eventPassOffers}});
                                } else {
                                    navigate('/checkout-result', { state: {success: false, isEventPass: +selectedOffer.offerType === +OFFER_TYPES.eventPassOffers} });
                                }
                            },
                            onError: (error, component) => {
                                // console.error(error.name, error.message, error.stack, component);
                                localStorage.removeItem('selectedOffer');
                                navigate('/checkout-result', { state: {success: false} });
                            },
                            // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
                            // For example, this is 3D Secure configuration for cards:
                            paymentMethodsConfiguration: {
                                card: {
                                    hasHolderName: true,
                                    holderNameRequired: true,
                                    // billingAddressRequired: true
                                }
                            }
                        };

                        // Create an instance of AdyenCheckout using the configuration object.
                        const checkout = await AdyenCheckout(configuration);

                        // Create an instance of Drop-in and mount it to the container you created.
                        const dropinComponent = checkout.create('dropin').mount('#dropin-container');
                    },
                    onAccessDenied: () => {
                        localStorage.removeItem('selectedOffer');
                        navigate('/shop');
                    },
                    onError: (err) => {
                        setError({
                            hasError: true,
                            message: ENV === 'test' ? err.message : null
                        });
                    },
                }
            );
        } catch (err) {
            setError({
                hasError: true,
                message: ENV === 'test' ? err.message : null
            });
        }
    }

    useEffect(() => {
        // Fallback if there is no player - navigate to the login page
        if (!player) {
            navigate('/');
            return;
        }

        // Validate offer selection
        if (!selectedOffer || !selectedOffer.offerId) {
            toast.error(config.errors.WENT_WRONG);
            localStorage.removeItem('selectedOffer');
            navigate('/shop');
        }
    }, []);

    useEffect( () => {
        window.scrollTo(0, 0)
        // No valid offer/player -> the validation effect above is redirecting,
        // so don't open a payment session.
        if (!player || !selectedOffer || !selectedOffer.offerId) {
            return;
        }
        initAdyenSession();
    }, []);

    useEffect(() => {
        if (isFirstPageView.current) {
            if (ENV === 'live') {
                window.gtag('event', 'screen_view', {
                    'app_name': 'Siege shop',
                    'screen_name': 'page_checkout'
                });
                window.gtag('event', 'page_view', {
                    'app_name': 'Siege shop',
                    'page_title': 'Checkout'
                });
            } else {
                window.gtag('event', 'screen_view', {
                    'app_name': 'DEV Siege shop',
                    'screen_name': 'DEV page_checkout'
                });
                window.gtag('event', 'page_view', {
                    'app_name': 'DEV Siege shop',
                    'page_title': 'DEV Checkout'
                });
            }
            isFirstPageView.current = false;
        }
    }, []);

    return isLoading ? (
        <Loading />
    ) : (
        <div>
            <ContextMenuMobile windowSize={windowSize} config={config}  />
            <div className={styles.wrapper}>
                <div className={styles.title}>{config.checkout}</div>
                <div id="dropin-container"></div>
                <AnimatePresence>
                    {error.hasError ? <PopupInfo text={error.message} setError={setError} /> : null}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Checkout;